import { useState } from 'react'
import { DollarSign, FileText, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react'
import { dataRoomService } from '@/services/dataRoomService'
import { RFQRequest } from '@/types/project'
import { Project } from '@/types/project'
import { Modal } from './Modal'
import { Button, Input } from '@/components/atoms'
import { Form } from '@/components/molecules'
import { parseNumberInput } from '@/utils/format'

export interface ProjectRFQModalProps {
  isOpen: boolean
  onClose: () => void
  project: Project | null
  onRFQSubmitted: () => void
}

export const ProjectRFQModal = ({ isOpen, onClose, project, onRFQSubmitted }: ProjectRFQModalProps) => {
  const [formData, setFormData] = useState({
    tranche_size: '',
    valuation: '',
    type: 'BUY' as 'BUY' | 'SELL'
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [showFullDescription, setShowFullDescription] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!project) return

    setLoading(true)
    setError('')
    setSuccess(false)

    try {
      // Parse the input values (handles formats like 100k, 1m, 1B, 100,000, etc.)
      const parsedTrancheSize = parseNumberInput(formData.tranche_size);
      const parsedValuation = formData.valuation ? parseNumberInput(formData.valuation) : undefined;

      if (isNaN(parsedTrancheSize) || parsedTrancheSize <= 0) {
        setError('Please enter a valid amount for tranche size.');
        return;
      }

      if (parsedValuation !== undefined && (isNaN(parsedValuation) || parsedValuation <= 0)) {
        setError('Please enter a valid valuation or leave it empty.');
        return;
      }

      const rfqData: RFQRequest = {
        project_id: project.project_id,
        tranche_size: parsedTrancheSize,
        type: formData.type,
        ...(parsedValuation !== undefined ? { valuation: parsedValuation } : {})
      }

      await dataRoomService.createRFQ(rfqData)
      setSuccess(true)
      onRFQSubmitted()
      
      // Close modal after 2 seconds to show success message
      setTimeout(() => {
        onClose()
        setSuccess(false)
        setFormData({ tranche_size: '', valuation: '', type: 'BUY' })
      }, 2000)
    } catch (err) {
      setError('Failed to submit RFQ. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const truncateDescription = (text: string) => {
    if (!text) return ''
    const maxChars = 80 // Strict limit for 2 lines
    
    if (text.length <= maxChars) {
      return text
    }
    
    // Truncate by words to avoid breaking words
    const truncated = text.substring(0, maxChars)
    const lastSpaceIndex = truncated.lastIndexOf(' ')
    
    if (lastSpaceIndex > 0) {
      return truncated.substring(0, lastSpaceIndex) + '...'
    }
    
    return truncated + '...'
  }

  const getProjectDescription = () => {
    const description = project?.description || 'Investment Opportunity'
    if (showFullDescription) {
      return description
    }
    return truncateDescription(description)
  }

  const shouldShowReadMore = () => {
    const description = project?.description || ''
    return description.length > 80
  }

  if (!project) return null

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Place Inquiry"
      contentClassName="max-w-lg"
      className="bg-black/60 backdrop-blur-md"
    >
      {success ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText size={32} className="text-green-500" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">RFQ Submitted Successfully!</h3>
          <p className="text-muted-foreground">
            Your request for quote has been submitted. Our team will review and get back to you.
          </p>
        </div>
      ) : (
        <>
          {/* Project Info */}
          <div className="mb-6 p-4 bg-muted/50 border border-border rounded-lg">
            <div className="flex items-start gap-3 mb-3">
              <img src={project.logo || ''} alt={project.project_name} className="w-10 h-10 rounded-full flex-shrink-0" />
              <div className="flex-1 min-w-0 pr-2">
                <h3 className="font-semibold text-foreground">{project.project_name}</h3>
                <div className="text-sm text-muted-foreground">
                  {getProjectDescription()}
                  {shouldShowReadMore() && (
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="ml-2 text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1"
                    >
                      {showFullDescription ? (
                        <>
                          Read less <ChevronUp size={14} />
                        </>
                      ) : (
                        <>
                          Read more <ChevronDown size={14} />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Funding Rounds:</span>
                <div className="font-medium text-foreground">{project.rounds?.length || 0} rounds</div>
              </div>
              <div>
                <span className="text-muted-foreground">Latest Round:</span>
                <div className="font-medium text-foreground">{project.rounds?.[0]?.round_name || 'N/A'}</div>
              </div>
            </div>
          </div>

          <Form onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            <Form.Field label="Request Type" required>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-foreground"
                required
              >
                <option value="BUY">Buy - I want to invest</option>
                <option value="SELL">Sell - I want to sell my position</option>
              </select>
            </Form.Field>

            <Form.Field label="Amount / Tranche Size ($)" required>
              <Input
                type="text"
                name="tranche_size"
                value={formData.tranche_size}
                onChange={handleChange}
                placeholder="e.g., 100000, 100k, 1m, 1B"
                leftElement={<DollarSign size={16} />}
                required
              />
            </Form.Field>

            <Form.Field label="Desired Valuation ($)">
              <Input
                type="text"
                name="valuation"
                value={formData.valuation}
                onChange={handleChange}
                placeholder="e.g., 100000, 100k, 1m, 1B (optional)"
                leftElement={<TrendingUp size={16} />}
              />
            </Form.Field>



            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? 'Submitting...' : 'Submit RFQ'}
              </Button>
            </div>
          </Form>
        </>
      )}
    </Modal>
  )
}

export default ProjectRFQModal 